import { mutationOptions, useMutation } from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import type { Account, EmailNotificationFrequency } from '@/api/types'
import { getSession } from '@/features/auth/session-store'

/**
 * The account's own settings.
 *
 * There is no account *query*: `AuthenticatedUser` arrives with the session and
 * is held in the session store, because the API middleware and the websocket
 * client both read the session synchronously from outside React. A second copy
 * in the Query cache would have to be kept in step with it for no gain.
 *
 * `user_updated` is also the one broadcast in the shell that is *not* filtered by
 * the acting tab's `web_socket_id` -- it goes to every workspace member including
 * the one who caused it. The patch is still applied locally rather than awaited,
 * because it is idempotent and a settings form that waits for a round trip
 * through the socket to show what you just chose reads as broken.
 */
/**
 * The one thing on the account this application decides.
 *
 * Identity is not among them, and is not on the serializer to send: the name comes
 * from the sign-in claims and is refreshed from them on every sign-in, so the
 * endpoint has nothing to say about it.
 */
export function updateAccountMutation() {
  return mutationOptions({
    mutationFn: (values: {
      email_notification_frequency: EmailNotificationFrequency
    }): Promise<Account> =>
      unwrap(getApi().PATCH('/api/user/account/', { body: values })),
    // Applied from the response rather than from what was sent, so what the
    // interface shows is what the server stored.
    onSuccess: (account) => {
      getSession().updateUser(account)
    },
  })
}

export function useUpdateAccount() {
  // No `QueryClient`: with the name gone this touches nothing but the session,
  // which is a Zustand store the whole application already reads directly.
  return useMutation(updateAccountMutation())
}
